import React, { useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import { dateLabel, money, recoverPaymentRecord } from '../lib/compat.js';
import { EmptyState } from './shared.jsx';

export default function AdminRecovery({ data, onRefresh }) {
  const [recovering, setRecovering] = useState({});
  const [recoverAllBusy, setRecoverAllBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const records = (data.paymentRecovery || []);
  const pending = records.filter((r) => !['order_created', 'order_created_by_admin', 'already_exists'].includes(r.status));
  const resolved = records.filter((r) => ['order_created', 'order_created_by_admin', 'already_exists'].includes(r.status));

  async function recoverOne(record) {
    setRecovering((prev) => ({ ...prev, [record.id]: true }));
    setMsg('');
    try {
      await recoverPaymentRecord(record);
      setMsg(`Order created for payment ${record.paymentId || record.id}.`);
      await onRefresh();
    } catch (e) {
      setMsg('Error: ' + (e.message || 'Recovery failed.'));
    } finally {
      setRecovering((prev) => ({ ...prev, [record.id]: false }));
    }
  }

  async function recoverAll() {
    setRecoverAllBusy(true);
    setMsg('');
    let count = 0;
    for (const rec of pending) {
      try { await recoverPaymentRecord(rec); count++; } catch (e) {}
    }
    setMsg(`Created ${count} missing order(s).`);
    await onRefresh();
    setRecoverAllBusy(false);
  }

  return (
    <div>
      {/* Header card */}
      <div style={{ background: 'linear-gradient(135deg,#FFF7ED,#FFFBF5)', border: '1px solid #FED7AA', borderRadius: 14, padding: '20px 24px', marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 40, height: 40, background: '#9A3412', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🛟</div>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#9A3412' }}>Paid Order Recovery</h2>
              <p style={{ margin: 0, fontSize: 13, color: '#B45309' }}>Razorpay-success payments that may not have an order yet</p>
            </div>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#92400E', lineHeight: 1.6, maxWidth: 560 }}>
            This panel shows payments where Razorpay reported success but no order was created. Recovery will <strong>not duplicate</strong> orders with the same payment ID — it is safe to run on all pending records.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0, alignItems: 'flex-start' }}>
          <button onClick={onRefresh} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#fff', border: '1px solid #FED7AA', borderRadius: 9, fontSize: 13, fontWeight: 600, color: '#9A3412', cursor: 'pointer' }}>
            <RefreshCcw size={14} /> Refresh
          </button>
          {pending.length > 0 && (
            <button onClick={recoverAll} disabled={recoverAllBusy} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', background: '#9A3412', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer' }}>
              {recoverAllBusy ? 'Creating…' : `Create All ${pending.length} Missing Orders`}
            </button>
          )}
        </div>
      </div>

      {msg && <div className={`banner compact ${msg.startsWith('Error') ? 'danger' : ''}`} style={{ marginBottom: 16 }}>{msg}</div>}

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 20 }}>
        {[
          { label: 'Total Records', value: records.length, color: 'var(--admin-navy)' },
          { label: 'Pending Recovery', value: pending.length, color: '#9A3412' },
          { label: 'Already Resolved', value: resolved.length, color: 'var(--admin-green)' }
        ].map(({ label, value, color }) => (
          <div key={label} style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--admin-border)', padding: '16px 18px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--admin-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color, fontFamily: 'monospace' }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Pending table */}
      <div className="adminCard" style={{ marginBottom: 20 }}>
        <div className="adminCardHead" style={{ background: '#FFF7ED', borderColor: '#FED7AA' }}>
          <h3 style={{ color: '#9A3412' }}>⚠️ Pending Recovery ({pending.length})</h3>
          <span style={{ fontSize: 12, color: '#B45309' }}>These payments succeeded but have no order yet</span>
        </div>
        {pending.length ? (
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>Payment ID</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Books</th>
                  <th>Total</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((rec) => (
                  <tr key={rec.id}>
                    <td>
                      <strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{rec.paymentId || rec.id}</strong>
                      <div style={{ fontSize: 10, color: 'var(--admin-muted)', marginTop: 2 }}>Status: {rec.status || 'pending'}</div>
                    </td>
                    <td>
                      <strong>{rec.delivery?.name || rec.name || 'Customer'}</strong>
                    </td>
                    <td style={{ fontSize: 12 }}>{rec.delivery?.phone || rec.studentPhone || rec.phone || '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      {(rec.items || []).slice(0, 2).map((i, idx) => (
                        <div key={idx}>{i.bt || i.bookTitle} ×{i.qty || 1}</div>
                      ))}
                      {(rec.items || []).length > 2 && <div style={{ color: 'var(--admin-muted)' }}>+{rec.items.length - 2} more</div>}
                    </td>
                    <td><strong style={{ color: 'var(--admin-navy)' }}>{money(Number(rec.total || 0))}</strong></td>
                    <td style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{dateLabel(rec.createdAt)}</td>
                    <td>
                      <button
                        type="button"
                        onClick={() => recoverOne(rec)}
                        disabled={!!recovering[rec.id]}
                        style={{ padding: '6px 14px', background: '#9A3412', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        {recovering[rec.id] ? 'Creating…' : 'Create Order'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="No pending records" body="All payments have corresponding orders." />
        )}
      </div>

      {/* Resolved table */}
      {resolved.length > 0 && (
        <div className="adminCard">
          <div className="adminCardHead">
            <h3 style={{ color: 'var(--admin-green)' }}>✅ Already Resolved ({resolved.length})</h3>
            <span style={{ fontSize: 12, color: 'var(--admin-muted)' }}>Orders were created or already existed</span>
          </div>
          <div className="tableWrap">
            <table>
              <thead>
                <tr><th>Payment ID</th><th>Customer</th><th>Total</th><th>Date</th><th>Resolution</th></tr>
              </thead>
              <tbody>
                {resolved.slice(0, 20).map((rec) => (
                  <tr key={rec.id}>
                    <td><strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{rec.paymentId || rec.id}</strong></td>
                    <td><strong>{rec.delivery?.name || rec.name || '—'}</strong></td>
                    <td>{money(Number(rec.total || 0))}</td>
                    <td style={{ fontSize: 12, color: 'var(--admin-muted)' }}>{dateLabel(rec.createdAt)}</td>
                    <td>
                      <span className={`badge ${rec.status === 'already_exists' ? 'b-ship' : 'b-dlvd'}`}>
                        {rec.status === 'already_exists' ? 'Already Existed' : 'Order Created'}
                      </span>
                      {rec.orderId && <div style={{ fontSize: 11, color: 'var(--admin-muted)', marginTop: 2 }}>{rec.orderId}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
